import React, { useEffect, useState } from 'react';

import { activeSearchHitColor, inactiveSearchHitColor, PDFCustomDataKey } from '@aline/common';
import type { Core } from '@pdftron/webviewer';
import { pdfTronCore } from 'src/components';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useEditorFindStateSelector } from 'src/editor/editor-find-state-registry';
import { FindPopper } from 'src/layout-engine';

export function PdfFindPopper(): JSX.Element | null {
  const documentViewer = useMaybeDocumentViewer();
  const search = useEditorFindStateSelector(state => state.search);
  const matchCase = useEditorFindStateSelector(state => state.matchCase);
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<Core.Search.SearchResult[]>([]);

  const navigateToNextResult = () => {
    setActiveResultIndex(prev => (prev + 1 >= searchResults.length ? 0 : prev + 1));
  };

  const navigateToPreviousResult = () => {
    setActiveResultIndex(prev => (prev - 1 < 0 ? searchResults.length - 1 : prev - 1));
  };

  const onClose = () => {
    if (!documentViewer) return;

    const annotationManager = documentViewer.getAnnotationManager();
    const searchAnnotations = annotationManager.getAnnotationsList().filter(annotation => {
      const value = annotation.getCustomData(PDFCustomDataKey.searchHighlight);
      return value === String(true);
    });
    const pageNumsToRedraw = new Set(searchAnnotations.map(annotation => annotation.PageNumber));

    documentViewer.clearSearchResults();
    annotationManager.deleteAnnotations(searchAnnotations);
    void Promise.all(
      pageNumsToRedraw
        .values()
        .map(pageNumber => annotationManager.drawAnnotations({ majorRedraw: true, pageNumber }))
        .toArray()
    );
  };

  useEffect(() => {
    if (searchResults.length)
      documentViewer?.setActiveSearchResult(searchResults[activeResultIndex]);
  }, [activeResultIndex, documentViewer, searchResults]);

  useEffect(() => {
    if (typeof search === 'string') setActiveResultIndex(0);
  }, [matchCase, search]);

  useEffect(() => {
    if (typeof search !== 'string' || !documentViewer) return;

    setSearchResults([]);
    documentViewer.clearSearchResults();
    documentViewer.setSearchHighlightColors({
      activeSearchResult: activeSearchHitColor.toString(),
      searchResult: inactiveSearchHitColor.toString(),
    });
    const results: Core.Search.SearchResult[] = [];

    const mode =
      pdfTronCore.Search.Mode.HIGHLIGHT | (matchCase ? pdfTronCore.Search.Mode.CASE_SENSITIVE : 0);
    documentViewer.textSearchInit(search, mode, {
      fullSearch: true,
      onDocumentEnd: () => {
        const sortedResults = sortSearchResultsByPosition(results);
        setSearchResults(sortedResults);
        documentViewer.displayAdditionalSearchResults(sortedResults);
      },
      onResult: (result: Core.Search.SearchResult) => {
        if (result.resultCode === pdfTronCore.Search.ResultCode.FOUND) results.push(result);
      },
    });
  }, [documentViewer, matchCase, search]);

  if (typeof search !== 'string') return null;

  return (
    <FindPopper
      navigateToNextResult={navigateToNextResult}
      navigateToPreviousResult={navigateToPreviousResult}
      onClose={onClose}
    />
  );
}

function sortSearchResultsByPosition(
  results: Core.Search.SearchResult[]
): Core.Search.SearchResult[] {
  return [...results].sort((a, b) => {
    const aResult = a;
    const bResult = b;

    if (aResult.pageNum !== bResult.pageNum) return aResult.pageNum - bResult.pageNum;

    const aQuad = aResult.quads?.[0];
    const bQuad = bResult.quads?.[0];
    if (!aQuad || !bQuad) return 0;

    const aY = Number(aQuad.y1 ?? aQuad.Hm ?? 0);
    const bY = Number(bQuad.y1 ?? bQuad.Hm ?? 0);
    const aX = Number(aQuad.x1 ?? aQuad.xt ?? 0);
    const bX = Number(bQuad.x1 ?? bQuad.xt ?? 0);
    if (Math.abs(aY - bY) > 1) return aY - bY;

    return aX - bX;
  });
}
